import { skipToken } from "@reduxjs/toolkit/query";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, BasicTable, Button, Icon, Modal, Spinner, Typography } from "@/components/atoms";
import {
  useGetBulkImportQuery,
  useGetClientBookingDataQuery,
  useRetryBulkImportMutation,
  useValidateBulkBookingMutation,
} from "@/redux/apis/clientBooking/clientBookingApi";
import { BulkBookingInputRow, BulkImportedRow, BulkRowError } from "@/redux/apis/clientBooking/types";
import { PointsOfInterest } from "@/redux/apis/clientPoi/types";
import { useAppSelector } from "@/redux/hooks";
import { accountIdSelector } from "@/redux/slices/account/selectors";
import { addToast, clsx } from "@/utils";
import { BOOKING_FIELDS, BookingFieldKey } from "./bookingFields";
import { formatPickupDate, formatPickupTime, parsePickupDate } from "./pickupFormat";
import { DETAIL_COLUMN, STICKY_EDIT_CELL, STICKY_EDIT_HEADER } from "./reportLayout";
import { EditRowModal, RowEdits } from "./steps/EditRowModal";
import { useBulkAddressResolution } from "./useBulkAddressResolution";

/** How often to re-ask while a run is in progress. Matches the Snackbar tracker's cadence. */
const POLL_MS = 2000;

/** The POI shape trimmed to what the validate endpoint takes. */
const toStopInput = (poi: PointsOfInterest) => ({
  address: poi.address,
  lat: poi.lat,
  lng: poi.lng,
  city: poi.city,
  state: poi.state,
  country: poi.country,
});

/** A correction the user has made but not yet sent. */
type PendingEdit = { source: BulkBookingInputRow; payload: unknown };

/**
 * The short zone name for an IANA timezone, read on a given date — "AEST", "AEDT", "PST".
 *
 * Read on the PICKUP's date rather than today's, because half the reason for printing it is trips
 * months out: an import run in July for a November pickup is AEST as you read it and AEDT when the car
 * turns up. Falls back to today only when the row's date won't parse.
 *
 * en-AU because the short names are what's wanted; en-US renders Australian zones as "GMT+11".
 */
const zoneAbbreviation = (timezone: string | null | undefined, on: Date | null): string | null => {
  if (!timezone) return null;

  try {
    const parts = new Intl.DateTimeFormat("en-AU", { timeZone: timezone, timeZoneName: "short" }).formatToParts(on ?? new Date());

    return parts.find((part) => part.type === "timeZoneName")?.value ?? null;
  } catch {
    // A zone the browser doesn't know shouldn't cost the row its detail line.
    return null;
  }
};

/**
 * The line the "What went wrong" cell shows.
 *
 * Lifted out of the row so the column can sort on exactly what the reader sees. Sorting on
 * `row.error` instead would order the table by the worker's original rejection while the cells
 * displayed a newer one from a correction that had just been refused.
 */
const reasonFor = (row: BulkImportedRow, edit?: PendingEdit, rejection?: BulkRowError[]): string => {
  if (edit) return "Updated — will be booked on retry";

  const refusal = rejection?.find((error) => error.level === "error") ?? rejection?.[0];
  if (refusal) return refusal.message;

  if (row.status === "pending") return "The import stopped before reaching this booking.";

  return row.error ?? "Unable to create this booking";
};

/**
 * The trip, and under it who is travelling and when.
 *
 * Both lines come off the row's `source` — the spreadsheet values it was built from — so a row stored
 * before source rows were kept renders the trip alone rather than a line of dashes and empty brackets.
 * The pickup is naive local time for the account the trips belong to, which is why the zone is stamped
 * on it: the reader is frequently in a different one.
 */
const BookingDetail = ({ source, timezone }: { source?: BulkBookingInputRow | null; timezone: string | null | undefined }) => {
  const trip = [source?.pickup, source?.destination].filter(Boolean).join(" → ");

  const name = [source?.passengerFirstName, source?.passengerLastName].filter(Boolean).join(" ").trim();
  const email = source?.passengerEmail?.trim();
  const who = name && email ? `${name} (${email})` : name || email || "";

  /*
   * Normalised for display, not echoed. A file holding "30/8/26" on one line and "31/08/2026" on the
   * next showed exactly that, side by side in the same column, as did "21:30" against "10:00 AM".
   * Anything unreadable falls back to the raw cell — that is the user's own text, and seeing it is how
   * they know what to fix.
   */
  const date = formatPickupDate(source?.pickupDate ?? "");
  const time = formatPickupTime(source?.pickupTime ?? "");
  const when = [date, time].filter(Boolean).join(", ");

  /*
   * Parsed through the SHARED reader, which widens a two-digit year first.
   *
   * This is what made a row dated "30/8/26" read "(GMT+10:04:52)": parsed as the year 26 AD, then
   * formatted against Sydney's Local Mean Time, from before standard timezones existed.
   */
  const zone = when ? zoneAbbreviation(timezone, parsePickupDate(source?.pickupDate ?? "")) : null;

  const detail = [who && `Booking for: ${who}`, when && `@ ${when}${zone ? ` (${zone})` : ""}`].filter(Boolean).join(" ");

  /*
   * Both lines WRAP rather than truncate.
   *
   * They used to be clipped to one line each, which kept every row the same height and hid the part
   * that matters. The commonest shape in a bulk file is the same trip repeated for different people,
   * so the passenger and the pickup time are exactly what tells two rows apart — and they sit at the
   * end of the line, where the ellipsis falls.
   *
   * It also matches the "What went wrong" column beside it, which has always wrapped for the same
   * reason: this is a screen you read in order to act on it.
   *
   * `break-words` rather than plain wrapping, because none of this is ours to trust — a mis-mapped
   * column can put a URL with no spaces in it here, and one of those already forced a column past the
   * table's width once.
   */
  return (
    <div className="min-w-0">
      <Typography className="block break-words text-neutral-black">{trip || "—"}</Typography>
      {detail && (
        <Typography variant="small-regular" className="block break-words text-neutral-dark-gray">
          {detail}
        </Typography>
      )}
    </div>
  );
};

interface BulkImportReportProps {
  bulkId: string;
  /** OBO: the client the import belongs to. Omitted for a client looking at their own. */
  accountId?: string;
  /**
   * What "Leave anyway" does, which is the one thing the report's two hosts can't share.
   *
   * From the modal it closes the modal — the user is already where they want to be. From the page,
   * where the reader arrived cold from an email, there is nowhere behind them, so it falls back to
   * home. Everything else on this screen is identical in both.
   */
  onLeave?: () => void;
  /**
   * The host's own chrome, level with the title — a Back link on the page, a close button in the
   * modal.
   *
   * Passed in rather than branched on, because it is the ONLY thing that differs between the two.
   * Everything from the title down is rendered here, once, so the page and the modal cannot drift
   * apart by someone styling one of them.
   */
  headerAction?: React.ReactNode;
}

/**
 * What happened to an import, and the one place a failure can be put right (DEV-1215).
 *
 * Retrying a row unchanged is close to useless. It fixes a transient failure — a worker that died
 * mid-run, a timeout, a one-off decline — but the failures people actually hit are about the data:
 * six passengers won't fit in a sedan, nobody could find that address. Re-sending the same row
 * produces the same rejection. So this screen edits the row first, through the same modal and the
 * same validate endpoint the review step uses, and only then queues it again.
 *
 * Rendered both as a modal (from the progress Snackbar, so it works in whichever portal the import
 * was started from) and as a page at a stable URL (what the completion email links to, days later).
 */
export const BulkImportReport = ({ bulkId, accountId, onLeave, headerAction }: BulkImportReportProps) => {
  const ownAccountId = useAppSelector(accountIdSelector);
  const scopedAccountId = accountId ?? ownAccountId;

  /**
   * Kept in state rather than derived inline, because what to poll depends on what the poll returns.
   * Starts on: an import opened from the Snackbar is usually mid-run, and a finished one turns it
   * off after a single request. A report opened from last week's email stops after one too.
   */
  const [pollInterval, setPollInterval] = useState(POLL_MS);
  const { data, isLoading, isError } = useGetBulkImportQuery({ bulkId, accountId }, { pollingInterval: pollInterval });

  const running = data?.status === "queued" || data?.status === "running";
  useEffect(() => setPollInterval(running ? POLL_MS : 0), [running]);

  /**
   * The transfer types, vehicles and extras the edit modal offers — scoped like the wizard scopes
   * them, so a corrected row picks from exactly what a manual booking for this client would.
   *
   * **The import's own account comes first, and that is the whole point.** The wizard can fall back to
   * the logged-in account because you got there by navigating the portal, so `activeAccount` is
   * populated. This screen is also a page above the portal trees, which one link in the completion
   * email drops you onto cold — no AuthCallback, no active account, so every picker in the editor came
   * up empty and Extras read "No extras configured". Asking the import who it belongs to is the only
   * answer that holds for a client, an assistant and an operator alike.
   */
  const bookingDataAccountId = data?.account_uuid ?? scopedAccountId;
  const { data: bookingData } = useGetClientBookingDataQuery(bookingDataAccountId ? { accountId: bookingDataAccountId } : skipToken);

  const [validate] = useValidateBulkBookingMutation();
  const [retry, { isLoading: isRetrying }] = useRetryBulkImportMutation();
  const { resolveAddresses } = useBulkAddressResolution();

  const [edits, setEdits] = useState<Record<number, PendingEdit>>({});
  /**
   * The last values tried for a row, whether they validated or not — what the editor reopens with.
   *
   * Separate from `edits`, which only ever holds corrections the server accepted, because the two
   * answer different questions. `edits` decides whether a row counts as updated and what gets sent on
   * retry; this decides what you see when you open the row again. Keeping only the former meant a
   * rejected attempt reopened showing the server's original values, quietly throwing away the fix
   * someone had just typed — the one moment they most need it back.
   */
  const [drafts, setDrafts] = useState<Record<number, BulkBookingInputRow>>({});
  /** Problems the server found with a correction — shown on the row, and again if it's reopened. */
  const [editErrors, setEditErrors] = useState<Record<number, BulkRowError[]>>({});
  const [editing, setEditing] = useState<BulkImportedRow>();
  /**
   * Row ids currently being re-checked, drawn as skeletons — the treatment the review step uses.
   *
   * Per row rather than one screen-wide flag, because the wait belongs to a row. The modal closes the
   * instant you save, exactly as it does in the wizard, and the row you edited pulses in place until
   * the server has priced it. Holding the modal open across a geocode and a validate call read as a
   * hang: the only progress text was in the toolbar, behind the modal's own overlay.
   */
  const [checkingRows, setCheckingRows] = useState<Set<number>>(new Set());
  /** Sort on the failure reason. Null is file order, which is what the table opens on. */
  const [reasonSort, setReasonSort] = useState<"asc" | "desc" | null>(null);
  /** The "Leave without retrying?" confirmation behind Ignore Failed Uploads. */
  const [confirmingIgnore, setConfirmingIgnore] = useState(false);

  const navigate = useNavigate();

  const correctedCount = Object.keys(edits).length;

  /**
   * Rows that still need something. Failures, plus anything the run never reached — the latter only
   * exists on an import that stopped partway, and lumping them in with the failures would tell
   * someone their booking was refused when nothing ever looked at it.
   */
  const outstanding = useMemo(() => (data?.rows ?? []).filter((row) => row.status === "failed" || row.status === "pending"), [data?.rows]);

  /**
   * The trips that exist now.
   *
   * Listed, not just counted. The bookings list can't answer "which of these came from that file" —
   * nothing there groups by import — so this is the only place the two are tied together. The
   * completion email lists them for the same reason; a report linked from that email showing less
   * than the email did would be an odd thing to arrive at.
   */
  const created = useMemo(() => (data?.rows ?? []).filter((row) => row.status === "booked"), [data?.rows]);

  /**
   * Failed rows whose outcome the server could not determine — the trip may exist.
   *
   * Recognised by the server's own wording, which is a coupling and worth naming as one: this reads a
   * sentence built in `BulkBookingImportResource::describeUnexpected()` and `BulkBookingImportTask`.
   * Reword either without touching this and the warning quietly stops appearing.
   *
   * There is no better signal today. Such a row has no `job_id` — but neither does any other failure,
   * so the status can't tell them apart. The robust fix is a flag on the row from the server, which is
   * a migration and belongs with the per-row retry work rather than here.
   */
  const unconfirmed = useMemo(
    () => outstanding.filter((row) => (row.error ?? "").includes("couldn't confirm whether this booking was created")),
    [outstanding]
  );

  /**
   * Every row in file order — what's shown WHILE the run is going, instead of the split below.
   *
   * The split is right for a finished import and wrong for a live one. A row starts as outstanding and
   * becomes created, so on each two-second poll rows left one table and reappeared in another, both
   * tables resized, and the page shuffled under the reader. Worse, an unprocessed row is `pending`,
   * which the outstanding table captions "The import stopped before reaching this booking" — flatly
   * untrue of an import that is still running, and shown for every row not yet reached.
   *
   * One list in the file's own order fixes both. Nothing moves; each row fills in where it already
   * sits. Same principle as the review step's skeleton rows, and the same column widths as the
   * finished view, so the switch at the end doesn't jump either.
   */
  const progressRows = useMemo(() => [...(data?.rows ?? [])].sort((a, b) => a.row_index - b.row_index), [data?.rows]);

  /**
   * The failures in the order the table shows them.
   *
   * File order until the reader asks otherwise, because a row number is how they find the line in
   * their own spreadsheet. Sorting by reason groups a run of identical failures — twenty rows refused
   * for the same past pickup — which is the case where the ordering earns its keep.
   */
  const sortedOutstanding = useMemo(() => {
    if (!reasonSort) return outstanding;

    const direction = reasonSort === "asc" ? 1 : -1;

    return [...outstanding].sort(
      (a, b) => direction * reasonFor(a, edits[a.id], editErrors[a.id]).localeCompare(reasonFor(b, edits[b.id], editErrors[b.id]))
    );
  }, [outstanding, reasonSort, edits, editErrors]);

  /** Re-check a corrected row and keep its fresh payload, or show why it still won't book. */
  const applyEdit = useCallback(
    async (row: BulkImportedRow, rowEdits: RowEdits) => {
      /*
       * Saving without changing anything is not a correction.
       *
       * `edits` is what turns a row green and labels it "Updated — will be booked on retry", and it used
       * to be set by any save the validate endpoint accepted — including one where every field still
       * held the value it arrived with. Opening a row to read it and closing with Save Changes marked
       * it fixed.
       *
       * Which is worse than cosmetic on the commonest failure of all: validation carries no clock
       * rule, so an unchanged past pickup validates perfectly here and is refused again the instant
       * the worker tries to book it. The screen would promise a fix that was never made.
       *
       * Nothing is lost by dropping it — retry re-queues every failed row whether a correction was
       * sent for it or not, so an unchanged row is still tried again.
       */
      const unchanged = BOOKING_FIELDS.every((field) => (rowEdits.values[field.key] ?? "") === (row.source?.[field.key] ?? ""));

      if (unchanged) {
        // Back to matching what the server holds, so neither a queued correction nor a kept draft
        // applies any more.
        setEdits((current) => {
          const next = { ...current };
          delete next[row.id];
          return next;
        });
        setDrafts((current) => {
          const next = { ...current };
          delete next[row.id];
          return next;
        });
        setEditErrors((current) => {
          const next = { ...current };
          delete next[row.id];
          return next;
        });
        setEditing(undefined);
        return;
      }

      /*
       * Close first, then check — the wizard's order.
       *
       * The row carries the wait from here on, as a skeleton, so there's somewhere for the delay to
       * live that isn't hidden behind an overlay.
       */
      setEditing(undefined);
      setCheckingRows((current) => new Set(current).add(row.id));

      try {
        const source: BulkBookingInputRow = {
          ...(row.source ?? { row_index: row.row_index }),
          ...rowEdits.values,
          row_index: row.row_index,
        };

        // Remembered before the outcome is known, so a rejection still reopens on what was typed.
        setDrafts((current) => ({ ...current, [row.id]: source }));

        // Anything picked from autocomplete is already resolved; only look up what was typed.
        const resolved = new Map<string, PointsOfInterest>();
        Object.values(rowEdits.resolved).forEach((poi) => poi && resolved.set(poi.address, poi));

        const unknown = [source.pickup ?? "", source.destination ?? ""]
          .map((address) => address.trim())
          .filter((address) => address && !resolved.has(address));

        if (unknown.length > 0) {
          (await resolveAddresses(unknown)).forEach((poi, address) => resolved.set(address, poi));
        }

        const pickup = resolved.get((source.pickup ?? "").trim());
        const destination = resolved.get((source.destination ?? "").trim());

        const response = await validate({
          rows: [
            {
              ...source,
              resolvedPickup: pickup ? toStopInput(pickup) : null,
              resolvedDestination: destination ? toStopInput(destination) : null,
            },
          ],
          accountId,
        }).unwrap();

        const checked = response.rows[0];

        if (!checked?.payload) {
          /*
           * Still not bookable. The rejection goes onto the row, which is where the first one was
           * read, and `drafts` holds what was typed so reopening carries on from the attempt.
           */
          setEditErrors((current) => ({ ...current, [row.id]: checked?.errors ?? [] }));
          setEdits((current) => {
            const next = { ...current };
            delete next[row.id];
            return next;
          });
          return;
        }

        setEdits((current) => ({ ...current, [row.id]: { source, payload: checked.payload } }));
        setEditErrors((current) => {
          const next = { ...current };
          delete next[row.id];
          return next;
        });
      } catch {
        addToast("danger", "We couldn't check that booking. Check your connection and try again.");
      } finally {
        setCheckingRows((current) => {
          const next = new Set(current);
          next.delete(row.id);
          return next;
        });
      }
    },
    [accountId, resolveAddresses, validate]
  );

  const runRetry = useCallback(async () => {
    try {
      await retry({
        bulkId,
        accountId,
        rows: Object.entries(edits).map(([id, edit]) => ({ id: Number(id), payload: edit.payload, source: edit.source })),
      }).unwrap();

      setEdits({});
      addToast("success", "Those bookings are being created. This page will update as they go.");
    } catch {
      addToast("danger", "We couldn't start the retry. Check your connection and try again.");
    }
  }, [accountId, bulkId, edits, retry]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (isError || !data) {
    return <Alert type="danger" message="We couldn't load this import. It may have been removed." />;
  }

  return (
    // gap-5 is the design's 20px rhythm — title block, summary, alert, table all sit that far apart.
    <div className="flex flex-col gap-5">
      <div className="flex items-start justify-between gap-4">
        {/* gap-1 — the file name sits 4px under the title, tighter than everything else, so the two
            read as one block rather than two stacked items. */}
        <div className="flex min-w-0 flex-col gap-1">
          <Typography variant="title">Import Report</Typography>
          {data.file_name && (
            <Typography variant="paragraph" className="truncate text-neutral-dark-gray">
              File name: <span className="text-neutral-black underline">{data.file_name}</span>
            </Typography>
          )}
        </div>
        {headerAction}
      </div>

      <ImportSummary
        booked={data.booked_count}
        failed={data.failed_count}
        notAttempted={data.not_attempted_count}
        total={data.total_rows}
        running={running}
      />

      {/* Alert carries the colours and the bold per-type icon, the same way the Review step's banner
          does — the two screens report the same kind of thing and should look like it. */}
      {data.error && <Alert type="danger" message={data.error} />}

      {outstanding.length > 0 && !running && (
        <Alert
          type="warning"
          message={`${outstanding.length} ${
            outstanding.length === 1 ? "booking couldn't" : "bookings couldn't"
          } be created. Fix the issues below and retry — successful bookings won't be duplicated.`}
        />
      )}

      {/*
       * The one case where the line above doesn't hold.
       *
       * "Successful bookings won't be duplicated" is true of every row we RECORDED as booked. A row
       * whose outcome we couldn't determine was never recorded either way — the trip may exist and
       * carry no job id against it — so retrying could create it a second time on a real account.
       *
       * Named rather than described, because Retry is all-or-nothing: telling someone to "check
       * first" is only useful if they know which row to check.
       */}
      {unconfirmed.length > 0 && !running && (
        <Alert
          type="danger"
          message={`${unconfirmed.length === 1 ? "Row" : "Rows"} ${unconfirmed
            .map((row) => row.row_index + 1)
            .join(", ")} may already have been booked — we couldn't confirm. Check Booking Manager before retrying, or ${
            unconfirmed.length === 1 ? "it could be created" : "they could be created"
          } twice.`}
        />
      )}

      {/* While the worker is still going: one list, in file order, filling in place. */}
      {running && (
        <BasicTable className="max-h-[45vh]">
          <BasicTable.Row>
            <BasicTable.HeaderCell className="sticky top-0 z-10 w-20">Row</BasicTable.HeaderCell>
            <BasicTable.HeaderCell className={clsx("sticky top-0 z-10", DETAIL_COLUMN)}>Booking Detail</BasicTable.HeaderCell>
            <BasicTable.HeaderCell className="sticky top-0 z-10 min-w-[280px]">Status</BasicTable.HeaderCell>
            {/* Empty, matching the finished view's action column — pinned the same way too, or the
                table would shift sideways the moment the run ends. */}
            <BasicTable.HeaderCell className={STICKY_EDIT_HEADER} />
          </BasicTable.Row>
          <>
            {progressRows.map((row) =>
              row.status === "booked" || row.status === "failed" ? <ProgressRow key={row.id} row={row} timezone={data.timezone} /> : <PendingRow key={row.id} />
            )}
          </>
        </BasicTable>
      )}

      {outstanding.length > 0 && !running && (
        <>
          <BasicTable className="max-h-[45vh]">
            <BasicTable.Row>
              <BasicTable.HeaderCell className="sticky top-0 z-10 w-20">Row</BasicTable.HeaderCell>
              <BasicTable.HeaderCell className={clsx("sticky top-0 z-10", DETAIL_COLUMN)}>Booking Detail</BasicTable.HeaderCell>
              <BasicTable.HeaderCell className="sticky top-0 z-10 min-w-[280px]">
                {/*
                 * Sort indicators follow DataTableHeader's: double-arrow when unsorted, a single bold
                 * chevron once a direction is chosen. The house pattern, so a sortable column looks
                 * sortable in the same way everywhere.
                 */}
                <button
                  type="button"
                  className="flex select-none items-center gap-1"
                  onClick={() => setReasonSort((current) => (current === "asc" ? "desc" : current === "desc" ? null : "asc"))}
                  aria-label="Sort by what went wrong"
                >
                  What went wrong
                  {reasonSort === "asc" ? (
                    <Icon name="ArrowDown2" variant="Bold" className="inline h-4 w-4 shrink-0" />
                  ) : reasonSort === "desc" ? (
                    <Icon name="ArrowUp2" variant="Bold" className="inline h-4 w-4 shrink-0" />
                  ) : (
                    <Icon name="double-arrow" isCustom className="inline h-4 w-4 shrink-0" />
                  )}
                </button>
              </BasicTable.HeaderCell>
              {/* Pinned like the wizard's. These four columns fit a desktop, but not a narrow window,
                  and Edit is the only thing in the table anyone can press. */}
              <BasicTable.HeaderCell className={STICKY_EDIT_HEADER} />
            </BasicTable.Row>
            <>
              {sortedOutstanding.map((row) =>
                // Being re-checked after an edit: a placeholder, the same as the review step draws
                // for a row the server hasn't answered for yet, so it's clear the new values are
                // still being priced.
                checkingRows.has(row.id) ? (
                  <PendingRow key={row.id} />
                ) : (
                  <OutstandingRow
                    key={row.id}
                    row={row}
                    edit={edits[row.id]}
                    // The rejection from the latest attempt, which is newer than whatever the worker
                    // recorded — and the reason this row is still here.
                    rejection={editErrors[row.id]}
                    timezone={data.timezone}
                    // A row with nothing stored behind it can be retried but not corrected — there's
                    // nothing to put in the editor. Only ever true for imports queued before source
                    // rows were kept.
                    canEdit={Boolean(row.source) && !running}
                    onEdit={() => setEditing(row)}
                  />
                )
              )}
            </>
          </BasicTable>
        </>
      )}

      {outstanding.length === 0 && !running && (
        <Alert type="success" message="Every booking was created. Nothing from this import needs another look." />
      )}

      {/*
       * The trips that exist — but only on a run with nothing left to fix.
       *
       * When something failed, this page is about the failures: listing the successes underneath them
       * is noise the reader can get from Booking Manager, and it pushes the thing they have to act on
       * up off the screen. When nothing failed there is no such competition, and this is the only
       * place a file is tied to the trips it produced — Booking History cannot answer "which of these
       * came from that upload".
       *
       * Held back while the run is going either way: those rows are in the single list above, and
       * showing them twice is what made the page shuffle on every poll.
       */}
      {created.length > 0 && !running && outstanding.length === 0 && (
        // No heading of its own — the summary line directly above already says "N bookings created",
        // and saying it twice on one screen made the count look like two different numbers.
        <BasicTable className="max-h-[45vh]">
          <BasicTable.Row>
            <BasicTable.HeaderCell className="sticky top-0 z-10 w-20">Row</BasicTable.HeaderCell>
            <BasicTable.HeaderCell className={clsx("sticky top-0 z-10", DETAIL_COLUMN)}>Booking Detail</BasicTable.HeaderCell>
            <BasicTable.HeaderCell className="sticky top-0 z-10 w-36">Booking No.</BasicTable.HeaderCell>
            <BasicTable.HeaderCell className="sticky top-0 z-10 w-28">Fare</BasicTable.HeaderCell>
          </BasicTable.Row>
          <>
            {created.map((row) => (
              <CreatedRow key={row.id} row={row} timezone={data.timezone} />
            ))}
          </>
        </BasicTable>
      )}

      {/*
       * The actions, under the table they act on.
       *
       * `mt-3` on top of the column's own 20px gap makes the design's 32px — deliberately more air
       * than the 20px rhythm above, so the buttons read as the end of the screen rather than another
       * band in the stack.
       *
       * Kept mounted through the run rather than appearing when it ends, because a button that
       * materialises the moment an import finishes is more of the shuffling this page has already had
       * enough of. Retry genuinely cannot run mid-flight — the server answers 409 while the worker
       * still holds rows — so it says so by being disabled, and drops the count until there is a
       * settled number to state.
       */}
      {(running || outstanding.length > 0) && (
        <div className="mt-3 flex items-center justify-end gap-3">
          {checkingRows.size > 0 && (
            <Typography variant="small" className="mr-auto text-neutral-dark-gray">
              {checkingRows.size === 1 ? "Checking that booking…" : `Checking ${checkingRows.size} bookings…`}
            </Typography>
          )}
          {checkingRows.size === 0 && correctedCount > 0 && (
            <Typography variant="small" className="mr-auto text-neutral-dark-gray">
              {correctedCount} {correctedCount === 1 ? "booking" : "bookings"} updated — not sent yet.
            </Typography>
          )}
          {/* Leaves without sending anything. Nothing is marked ignored server-side — the rows stay
              failed and the report keeps working, so the email's link can still pick this up later.
              The confirmation says as much. */}
          <Button variant="tertiary" size="lg" disabled={running || isRetrying} onClick={() => setConfirmingIgnore(true)}>
            Ignore Failed Uploads
          </Button>
          <Button size="lg" disabled={running || isRetrying} onClick={runRetry}>
            {isRetrying
              ? "Starting…"
              : running
              ? "Retry bookings"
              : `Retry ${outstanding.length} ${outstanding.length === 1 ? "booking" : "bookings"}`}
          </Button>
        </div>
      )}

      {editing && (
        <EditRowModal
          // Remounts per row, so the draft state inside starts from that row's values rather than
          // the previous one's.
          key={editing.id}
          open
          onClose={() => setEditing(undefined)}
          rowNumber={editing.row_index + 1}
          values={BOOKING_FIELDS.reduce(
            (acc, field) => ({
              ...acc,
              // The last attempt wins over what's stored, accepted or not — otherwise reopening a row
              // discards what the user typed, which after a rejection is exactly what they need back.
              [field.key]: (drafts[editing.id] ?? editing.source)?.[field.key] ?? "",
            }),
            {} as Record<BookingFieldKey, string>
          )}
          errors={editErrors[editing.id] ?? []}
          bookingData={bookingData}
          onSave={(rowEdits) => applyEdit(editing, rowEdits)}
        />
      )}

      {/*
       * Ignoring is leaving, so the confirmation is phrased as leaving.
       *
       * Nothing is destroyed by it — the rows keep their failed status and this report stays at its
       * URL, which the completion email links to — so the copy points at the recoverable path rather
       * than warning about a loss that isn't happening.
       */}
      <Modal
        open={confirmingIgnore}
        onClose={() => setConfirmingIgnore(false)}
        variant="web-small"
        className="sm:w-[400px]"
        title="Leave without retrying?"
        description={`The ${outstanding.length} ${
          outstanding.length === 1 ? "booking that failed won't" : "bookings that failed won't"
        } be created. You can re-upload the file to try again.`}
        renderButtons={() => (
          <>
            <Button variant="tertiary" size="lg" className="flex-1" onClick={() => setConfirmingIgnore(false)}>
              Keep editing
            </Button>
            <Button
              variant="custom"
              size="lg"
              className="flex-1 bg-danger text-white hover:bg-danger/90"
              onClick={() => {
                setConfirmingIgnore(false);
                // Falls back to home, as the page's own Back control does — a report reached from an
                // email has no history to pop, and where "home" is depends on the reader's portal.
                if (onLeave) onLeave();
                else navigate("/");
              }}
            >
              Leave anyway
            </Button>
          </>
        )}
      />
    </div>
  );
};

interface ImportSummaryProps {
  booked: number;
  failed: number;
  notAttempted: number;
  total: number;
  running: boolean;
}

/**
 * One outcome per icon, rather than one icon for the whole run.
 *
 * "97 of 100 booked" hides which of the other three were refused and which were never looked at, and
 * those call for different things from the reader. Zeroes are dropped, so a clean import reads as a
 * single green line rather than one with an emphatic "0 failed" beside it.
 *
 * The headline of the screen, and so the largest thing on it — an `h2`, above the `title`-sized
 * "Import Report" that labels it. What happened matters more than what the screen is called.
 */
const ImportSummary = ({ booked, failed, notAttempted, total, running }: ImportSummaryProps) =>
  running ? (
    <div className="flex items-center gap-2.5">
      {/* A spinner, because the count beside it only moves every couple of seconds and a static
          icon next to a static number reads as a page that has stopped working. */}
      <Spinner className="mr-0 h-6 w-6" />
      <Typography variant="h2">{`Importing — ${booked + failed} of ${total} done`}</Typography>
    </div>
  ) : (
    // gap-2.5 is the design's 10px, and it does all the spacing on this row: icon to text, text to
    // dot, dot to the next icon are one measurement, so the rhythm holds however many outcomes show.
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2">
      {outcomesOf(booked, failed, notAttempted).map((outcome, index) => (
        <Fragment key={outcome.key}>
          {/* A drawn 4px dot, not a typographic "·" — that one sits on the text baseline and scales
              with the font, where this has to stay a fixed circle centred against 24px headings. */}
          {index > 0 && <span aria-hidden className="h-1 w-1 shrink-0 rounded-full bg-neutral-silver-gray" />}
          <Outcome {...outcome} />
        </Fragment>
      ))}
    </div>
  );

/**
 * Which outcomes the summary line states, in order.
 *
 * Zeroes are dropped — including "created", so an import where nothing worked doesn't open with a
 * green tick beside a 0, which reads as reassurance it hasn't earned. The one exception is a run with
 * no failures at all: "0 bookings created" is then the only true thing to say, and an empty line
 * would say nothing.
 *
 * "Not attempted" is never folded into the failures. "We tried and it was rejected" and "we never got
 * to it" lead the reader somewhere different.
 */
const outcomesOf = (booked: number, failed: number, notAttempted: number) =>
  [
    (booked > 0 || failed + notAttempted === 0) && {
      key: "created",
      icon: "TickCircle",
      tone: "text-success",
      count: booked,
      noun: "created",
    },
    failed > 0 && { key: "failed", icon: "InfoCircle", tone: "text-danger", count: failed, noun: "failed", flipIcon: true },
    notAttempted > 0 && { key: "not-attempted", icon: "Danger", tone: "text-warning", count: notAttempted, noun: "not attempted" },
  ].filter(Boolean) as { key: string; icon: string; tone: string; count: number; noun: string; flipIcon?: boolean }[];

/** One "N bookings <verb>" pair in the summary line. */
const Outcome = ({
  icon,
  tone,
  count,
  noun,
  flipIcon,
}: {
  icon: string;
  tone: string;
  count: number;
  noun: string;
  /** InfoCircle is drawn dot-up, so it needs rotating to read as "!". */
  flipIcon?: boolean;
}) => (
  <span className="flex items-center gap-2.5">
    <Icon name={icon} variant="Bold" size="lg" className={clsx("shrink-0", tone, flipIcon && "rotate-180")} />
    <Typography variant="h2" className="text-neutral-black">
      {count} {count === 1 ? "booking" : "bookings"} {noun}
    </Typography>
  </span>
);

/** A trip that exists, with the two things that let someone find it again: its number and its fare. */
const CreatedRow = ({ row, timezone }: { row: BulkImportedRow; timezone: string | null }) => (
  <BasicTable.Row>
    {/* Edge on the first cell, not the row — see OutstandingRow: a `<tr>` border loses the border
          collapse and paints on the first row only. */}
    <BasicTable.Cell className="w-20 border-l-4 border-l-success">
      {/* 1-based, matching the row the user counts in their own file. */}
      <Typography className="text-neutral-black">{row.row_index + 1}</Typography>
    </BasicTable.Cell>
    <BasicTable.Cell className={DETAIL_COLUMN}>
      <BookingDetail source={row.source} timezone={timezone} />
    </BasicTable.Cell>
    <BasicTable.Cell className="w-36">
      <Typography className="text-neutral-black">{row.display_job_id ?? "\u2014"}</Typography>
    </BasicTable.Cell>
    <BasicTable.Cell className="w-28">
      {/* Absent only if the job vanished between booking and this page being opened. */}
      <Typography className="text-neutral-black">{row.total !== null ? `$${row.total.toFixed(2)}` : "\u2014"}</Typography>
    </BasicTable.Cell>
  </BasicTable.Row>
);

/**
 * A row being re-checked, drawn the way the review step draws one it hasn't heard back about.
 *
 * Four pulsing bars on the report's four columns. The point is that the wait is visible where the
 * change was made: the editor closes the moment you save, and this holds the row's place until the
 * server has re-priced it. It replaced a modal that stayed open with no pending state of its own and
 * read, fairly, as a hang.
 */
const PendingRow = () => (
  <BasicTable.Row>
    <BasicTable.Cell className="w-20">
      <div className="h-4 w-6 animate-pulse rounded bg-neutral-gray" />
    </BasicTable.Cell>
    <BasicTable.Cell className={DETAIL_COLUMN}>
      <div className="h-4 w-full animate-pulse rounded bg-neutral-gray" />
    </BasicTable.Cell>
    <BasicTable.Cell className="min-w-[280px]">
      <div className="h-4 w-full animate-pulse rounded bg-neutral-gray" />
    </BasicTable.Cell>
    {/* Pinned like the rows it stands in for, so the column doesn't jump as they resolve. */}
    <BasicTable.Cell className={STICKY_EDIT_CELL}>
      <div className="h-4 w-12 animate-pulse rounded bg-neutral-gray" />
    </BasicTable.Cell>
  </BasicTable.Row>
);

/**
 * One row the worker has already answered, while the rest of the run is still going.
 *
 * Deliberately says what happened and offers nothing to do about it: mid-run there is nothing useful
 * to press, and a retry cannot be queued while the worker still holds rows. The failures become
 * editable the moment the run ends, in the table below.
 */
const ProgressRow = ({ row, timezone }: { row: BulkImportedRow; timezone: string | null }) => {
  const booked = row.status === "booked";
  // The number and the fare, which is what the finished view shows for a created row — so the same
  // row reads the same before and after the run ends.
  const outcome = [row.display_job_id, row.total !== null ? `$${row.total.toFixed(2)}` : null].filter(Boolean).join(" · ");

  return (
    <BasicTable.Row className={clsx(booked ? "bg-success-light/30" : "bg-danger-light/30")}>
      <BasicTable.Cell className={clsx("w-20 border-l-4", booked ? "border-l-success" : "border-l-danger")}>
        <Typography className="text-neutral-black">{row.row_index + 1}</Typography>
      </BasicTable.Cell>

      {/* The same detail the finished view shows, so a row doesn't change shape underneath the reader
          when the run ends — it used to be the trip alone, on one truncated line. */}
      <BasicTable.Cell className={DETAIL_COLUMN}>
        <BookingDetail source={row.source} timezone={timezone} />
      </BasicTable.Cell>

      <BasicTable.Cell className="min-w-[280px]">
        <span className="flex min-w-0 items-center gap-2">
          <Icon
            name={booked ? "TickCircle" : "InfoCircle"}
            variant="Bold"
            size="md"
            className={clsx("shrink-0", booked ? "text-success" : "rotate-180 text-danger")}
          />
          <Typography className={clsx("block break-words", booked ? "text-success" : "text-danger")}>
            {booked ? outcome || "Booked" : row.error ?? "Unable to create this booking"}
          </Typography>
        </span>
      </BasicTable.Cell>

      {/* Empty, but pinned and tinted like the finished view's action column, so ending the run
          doesn't reflow the table sideways. */}
      <BasicTable.Cell className={clsx("relative", STICKY_EDIT_CELL)}>
        <span
          aria-hidden
          className={clsx("pointer-events-none absolute inset-0", booked ? "bg-success-light/30" : "bg-danger-light/30")}
        />
      </BasicTable.Cell>
    </BasicTable.Row>
  );
};

interface OutstandingRowProps {
  row: BulkImportedRow;
  edit?: PendingEdit;
  /** Why the newest attempt was refused, if there was one. Newer than what the worker recorded. */
  rejection?: BulkRowError[];
  /** The account's zone, stamped on the pickup time in the detail line. */
  timezone: string | null;
  canEdit: boolean;
  onEdit: () => void;
}

/**
 * One row that needs another look.
 *
 * Styled the way the Review step styles a broken row — a coloured left edge and a tinted reason cell
 * — because it is reporting the same kind of thing to the same person, just later. A row the user has
 * already corrected turns green, so a long list shows at a glance what's left.
 *
 * A failure and a row nobody reached get different colours on purpose: one was refused, the other was
 * never looked at, and they lead to different expectations about what a retry will do.
 */
const OutstandingRow = ({ row, edit, rejection, timezone, canEdit, onEdit }: OutstandingRowProps) => {
  // A correction that hasn't been sent yet still describes this row better than what's stored.
  const source = edit?.source ?? row.source;
  const untouched = row.status === "pending";
  // The newest reason wins: a correction that was just refused explains this row better than the
  // failure the worker recorded before anyone touched it. Shared with the column's sort, so the
  // ordering and the text can't disagree.
  const reason = reasonFor(row, edit, rejection);

  return (
    <BasicTable.Row className={clsx(edit ? "bg-success-light/30" : untouched ? "bg-warning-light/30" : "bg-danger-light/30")}>
      {/*
       * The status edge lives on the first CELL, not the row.
       *
       * `BasicTable` collapses its borders, and a `<tr>`'s own left border loses that collapse against
       * the cells beside it — so the accent painted on the first row of a table and silently nowhere
       * else, which read as a colour that only sometimes meant something.
       */}
      <BasicTable.Cell className={clsx("w-20 border-l-4", edit ? "border-l-success" : untouched ? "border-l-warning" : "border-l-danger")}>
        {/* 1-based — it has to match the row the user counts in their own file. */}
        <Typography className="text-neutral-black">{row.row_index + 1}</Typography>
      </BasicTable.Cell>

      <BasicTable.Cell className={DETAIL_COLUMN}>
        <BookingDetail source={source} timezone={timezone} />
      </BasicTable.Cell>

      <BasicTable.Cell className={clsx("min-w-[280px]", !edit && (untouched ? "bg-warning-light" : "bg-danger-light"))}>
        <span className="flex min-w-0 items-center gap-2">
          {/* Circle for a rejection, triangle for a warning — the shapes Alert and the Review step
              already use. InfoCircle is drawn dot-up, so it needs rotating to read as "!". */}
          <Icon
            name={edit ? "TickCircle" : untouched ? "Danger" : "InfoCircle"}
            variant="Bold"
            size="md"
            className={clsx("shrink-0", edit ? "text-success" : untouched ? "text-warning" : "rotate-180 text-danger")}
          />
          {/* break-words, because this string is not ours to trust. A reason can be a payment
              gateway's wording or, when something has genuinely broken, a file path with no spaces
              in it — one of those forced this column past the table's width and pushed the row
              number off the left edge. */}
          <Typography className={clsx("block break-words", edit ? "text-success" : untouched ? "text-neutral-black" : "text-danger")}>
            {reason}
          </Typography>
        </span>
      </BasicTable.Cell>

      <BasicTable.Cell className={clsx("relative", STICKY_EDIT_CELL)}>
        {/* The row's own tint, painted back over the opaque backing the pin needs, so a pinned cell
            still reads as part of its row. */}
        <span
          aria-hidden
          className={clsx(
            "pointer-events-none absolute inset-0",
            edit ? "bg-success-light/30" : untouched ? "bg-warning-light/30" : "bg-danger-light/30"
          )}
        />
        {canEdit && (
          <Button variant="tertiary" size="sm" startIcon="Edit2" onClick={onEdit} className="relative">
            Edit
          </Button>
        )}
      </BasicTable.Cell>
    </BasicTable.Row>
  );
};
